import { buildUserDefinedCodeFields } from '../shared/userDefinedCodeFields';

/**
 * Dynamic-form schemas for the WM Rate Schedule Profile screen (WMRATESCHEDULEDP), migrated from the
 * Delphi form uWMD0005 "WM Rate Schedules Profile Entry" (caption "WM Enter Rate Schedule Profiles").
 * Field labels, group headings, widths and the User Defined code types are taken verbatim from
 * uWMD0005.dfm and its data module uWMM0005.dfm.
 */

const TABLE = 'WMRATESCHEDULEDP';

// The CodeType on edtUserDefinedCode1..5 in uWMD0005.dfm. Matches
// Argos.Core/Constants/RateScheduleDpConstants.CodeTypes on the server side.
const USER_DEFINED_CODE_TYPES = ['67', '68', '69', '70', '71'];

// Its own gridKey: the lookup grid persists column state per key.
const CODE_LOOKUP_GRID_KEY = '1BF16CA9-F8D5-476C-BBC1-9F066C391B9C';

// WMRATESCHEDULEDP widths - the TAbStringField Sizes in uWMM0005.dfm. Match
// RateScheduleDpConstants.FieldLengths.
const PROFILE_CODE_LENGTH = 10;
const DESCRIPTION_LENGTH = 40;
const USER_DEFINED_CODE_LENGTH = 10;

const userDefinedCodeFields = (forSearch) => buildUserDefinedCodeFields({
    codeTypes: USER_DEFINED_CODE_TYPES,
    gridKey: CODE_LOOKUP_GRID_KEY,
    tableName: TABLE,
    maxLength: USER_DEFINED_CODE_LENGTH,
    forSearch,
});

/**
 * Find-screen search panel.
 *
 * One field per group on purpose: FormField renders every group as a Bootstrap `col-md-4`, so three
 * single-field groups put Record # / Profile Code / Description on one line. Delphi's navigator
 * searched on CriteriaFields = (PROFILECODE); Record #, Description and the five codes are added
 * because the listing grid exposes them. COMMENT is deliberately absent - it is a SQL Server `text`
 * column, and FillFilters' "contains" compiles to CHARINDEX, which it rejects.
 *
 * Field keys are camelCase while the DTO properties they filter on are PascalCase; DataSourceLoader
 * resolves the selector case-insensitively.
 */
export const SearchLayout = [
    {
        "groupId": 1,
        "fields": [
            { "name": "recordNumber", "localizedField": "KEYSEQUENCE", "tableName": TABLE, "label": "Record #", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 2,
        "fields": [
            { "name": "profileCode", "tableName": TABLE, "label": "Profile Code", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 3,
        "fields": [
            { "name": "description", "tableName": TABLE, "label": "Description", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        // Hidden on the listing grid, so hidden here too - the dynamic-form customiser then exposes
        // exactly the same set either way.
        "groupId": 4,
        "fields": userDefinedCodeFields(true)
    }
];

/**
 * The Delphi "Primary" tab.
 *
 * uWMD0005 has no tab set: every control lives in a top panel, with a blank group box below it. The
 * web form renders that panel's groups as the Primary tab's two columns, in the same left-to-right
 * order the desktop screen shows them:
 *
 *   grpTopLeft   - Record #, Profile Code, Description, Comment
 *   grpTopMiddle - "User Defined Codes", Code 1..5
 *
 * (grpTopRight holds only the Rate Schedule Defaults button, which is on the toolbar here.)
 */
export const PrimaryLayout = [
    {
        "groupId": 1,
        "label": "",
        "fields": [
            {
                "name": "RECORD",
                "localizedField": "KEYSEQUENCE",
                "tableName": TABLE,
                "label": "Record #",
                "type": "text",
                "required": false,
                "visible": true,
                "readOnly": true,
                "isLabelChanged": false,
            },
            {
                // "Profile Code" - the lblPROFILECODE caption in uWMD0005.dfm. (The field's
                // DisplayLabel reads "Profile", which is why the listing column says that.)
                // Required by approved deviation: uWMM0005 declares no NonBlankFields.
                "name": "PROFILECODE",
                "tableName": TABLE,
                "label": "Profile Code",
                "type": "text",
                "required": true,
                "maxLength": PROFILE_CODE_LENGTH,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "DESCRIPTION",
                "tableName": TABLE,
                "label": "Description",
                "type": "text",
                "required": false,
                "maxLength": DESCRIPTION_LENGTH,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                // COMMENT is SQL `text` and the Delphi control is a TAbMemo, so it renders multiline
                // (rule 9).
                "name": "COMMENT",
                "tableName": TABLE,
                "label": "Comment",
                "type": "textarea",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        // Delphi grpTopMiddle, headed by the hdrUserDefined column label.
        "groupId": 2,
        "label": "User Defined Codes",
        "fields": userDefinedCodeFields(false)
    }
];
